import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { getTimeAgo } from '../../../helpers'
import { useAppSelector } from '../../../hooks'
import { useGetAllVendorsQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import { FulfilmentCentre } from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { StrippedVendor } from '../../../redux-toolkits/vendors/vendors.types'
import { Avatar } from '../../avatar'
import { Button } from '../../button'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'

type V = StrippedVendor

export const SelectCreators = ({
  maxSelection,
  open,
  creators,
  setCreators,
  fulfillmentCenters,
  section,
  status,
}: {
  maxSelection?: number
  open?: boolean
  creators: V[]
  setCreators: (creators: V[]) => void
  fulfillmentCenters?: FulfilmentCentre[]
  section?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
  status?: 'active' | 'inactive'
}) => {
  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const [queryParam, setQueryParam] = useState<QueryProps>({
    skip: 0,
    limit: 50,
    search: undefined,
  })

  const {
    currentData: data,
    isFetching,
    isError,
    refetch,
  } = useGetAllVendorsQuery(
    {
      ...queryParam,
      search: queryParam.search || undefined,
      fulfillmentcenterId:
        (fulfillmentCenters ?? selectedFulfilmentCenters)
          .map((c) => c._id)
          .join(',') || undefined,
      section,
      status,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    },
  )

  const selectableCreators = (data?.data?.vendors ?? []).filter(
    (creator) => !status || creator.status === status,
  )

  return (
    <div
      className="
        flex flex-col gap-2
        cursor-pointer
        "
    >
      <div className="bg-white pb-2 !max-w-full sticky top-0 z-10">
        <SearchInput
          searchFunction={(search) => {
            setQueryParam({
              ...queryParam,
              search: search.trim() || undefined,
              skip: 0,
            })
          }}
          placeholder="Search Creators"
          className="!max-w-full"
        />
      </div>

      {isError && (
        <p role="alert">
          Unable to load creators.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              void refetch()
            }}
          >
            Retry
          </button>
        </p>
      )}
      {!isFetching && !isError && selectableCreators.length === 0 && (
        <p className="py-4 text-sm text-sec-black">
          {queryParam.search
            ? 'No creators match your search.'
            : 'No creators available.'}
        </p>
      )}
      <div className="w-full flex-1 overflow-y-scroll">
        <TableComponent
          rows={selectableCreators.map((creator) => {
            return {
              id: creator._id,
              content: [
                <Checkbox
                  className="!gap-6 !w-full"
                  disabled={
                    maxSelection !== undefined &&
                    creators.length >= maxSelection &&
                    creators.length != 1 &&
                    !creators.map((c) => c._id).includes(creator._id)
                  }
                  checkStatus={
                    creators.map((c) => c._id)?.includes(creator._id) ?? false
                  }
                  handleChange={(checked) => {
                    if (checked) {
                      const newItem: V = {
                        _id: creator._id,
                        name: creator.businessName || '',
                        vendorId: creator._id,
                        slug: '',
                        logo: creator.logo || '',
                        banner: creator.banner || '',
                        extendedBio: '',
                      }
                      const creatorsCopy: V[] = JSON.parse(
                        JSON.stringify(creators),
                      )
                      if (
                        maxSelection !== undefined &&
                        creatorsCopy.length >= maxSelection
                      ) {
                        creatorsCopy.shift()
                      }
                      creatorsCopy.push(newItem)
                      setCreators(creatorsCopy)
                    } else {
                      const creatorsCopy: V[] = JSON.parse(
                        JSON.stringify(creators),
                      )

                      setCreators(
                        creatorsCopy.filter(
                          (singlecreator) => creator._id !== singlecreator._id,
                        ),
                      )
                    }
                  }}
                  key={creator._id}
                  text={
                    <div className="flex !w-full gap-3 items-center">
                      <Avatar
                        image={creator.logo || undefined}
                        name={creator.businessName || undefined}
                        useRandomBgColor={false}
                      />

                      <div>
                        <p className="flex-1 text-base text-black font-normal">
                          {creator.businessName}
                        </p>

                        <p className="text-sm text-sec-black">
                          {getTimeAgo(creator.createdAt)}
                        </p>
                      </div>
                    </div>
                  }
                />,
              ],
            }
          })}
          loading={isFetching}
          isEmpty={false}
        />
      </div>
    </div>
  )
}

export const SelectCreatorModal = ({
  open,
  setOpen,
  onCloseCallback,
  onSuccessCallback,
  selectedCreators,
  maxSelection,
  loading = false,
  fulfillmentCenters,
  section,
  status,
}: {
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: V[]) => void
  selectedCreators?: V[]
  maxSelection?: number
  loading?: boolean
  fulfillmentCenters?: FulfilmentCentre[]
  section?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
  status?: 'active' | 'inactive'
  allowUnselect?: boolean
}) => {
  const [creators, setCreators] = useState<V[]>([])

  useEffect(() => {
    if (open) {
      setCreators(selectedCreators ?? [])
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback && onCloseCallback()
      }}
      title="Select Creators"
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <SelectCreators
          maxSelection={maxSelection}
          open={open}
          creators={creators}
          setCreators={setCreators}
          fulfillmentCenters={fulfillmentCenters}
          section={section}
          status={status}
        />

        <Button
          type="button"
          primary
          label="Continue"
          onClick={() => {
            onSuccessCallback?.(creators)
          }}
          loading={loading}
          disabled={loading || creators.length === 0}
          className="sticky bottom-0"
        />
      </div>
    </ModalComponent>
  )
}
